<template>
  <div>
    <span class="mb-1 font-medium">{{ title }}</span>
    <div class="mb-2">
      <div
        class="relative flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border-subtle bg-white p-10"
        :class="dragAreaClasses"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @mouseenter.prevent="isHovering = true"
        @mouseleave.prevent="isHovering = false"
        @drop.prevent="handleDrop"
        @click="triggerFileInput">
        <input
          ref="fileInput"
          type="file"
          :accept="allowedImageTypes.join(',')"
          class="hidden"
          @change="handleFileSelect" />

        <!-- No image -->
        <div v-if="!image">
          <span class="pb-1 text-center font-medium text-text-primary">
            {{ $t('image-upload.drag-image-here') }}
          </span>
          <span class="text-center text-sm text-text-secondary">
            {{ $t('image-upload.or-click-to-select') }}
          </span>
        </div>

        <!-- Preview image -->
        <div v-else>
          <DisplayImage
            class="max-h-40"
            :image="image" />
        </div>

        <!-- Remove preview -->
        <div
          v-if="image"
          class="absolute top-0.5 right-0.5 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-md bg-surface-sunken text-text-primary opacity-75 hover:opacity-90"
          @click.stop="onResetImageClick">
          <IconLucideTrash2 class="h-6 w-6" />
        </div>
      </div>
    </div>
    <InfoText
      class="mb-1"
      :text="errorText"
      theme="warning"
      @click="errorText = ''" />
    <div>
      <span class="text-sm text-text-secondary">{{ allowedImageTypesText }}</span>
      <span
        v-if="showTransparentBackgroundWarning"
        class="text-sm text-text-secondary">
        {{ $t('image-upload.transparency-expected-warning') }}
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import { useImageStore } from '@/stores/imageStore/imageStore'
import type {
  IImageBase,
  TAllowedImageContentTypes,
  TFileNameFormat,
} from '@team-milva/lab-models/image'
import { mapActions } from 'pinia'
import type { PropType } from 'vue'

export default {
  name: 'ImageUpload',
  emits: ['image-selected', 'image-removed'],
  props: {
    title: {
      type: String,
      required: true,
    },
    allowedImageTypes: {
      type: Array as PropType<TAllowedImageContentTypes[]>,
      required: true,
    },
    image: {
      type: [Object, null] as PropType<IImageBase | null>,
      required: true,
    },
    uploadPath: {
      type: String,
      required: true,
    },
    fileNameFormat: {
      type: String as PropType<TFileNameFormat>,
      default: '',
    },
    maxDim: {
      type: Number,
      required: false,
    },
    showTransparentBackgroundWarning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDragging: false as boolean,
      isHovering: false as boolean,
      errorText: '' as string,
    }
  },
  computed: {
    dragAreaClasses(): Record<string, boolean> {
      return {
        'border-border-strong bg-surface-muted': this.isDragging && !this.image,
        'hover:border-border-strong hover:bg-surface-muted':
          this.isHovering && !this.image,
        'h-40': !this.image,
      }
    },
    allowedImageTypesText(): string {
      const typesAsText = this.allowedImageTypes
        .map((type) => this.$t(`image-type.${type}`))
        .join(', ')
      const text = `${this.$t('image-upload.allowed-file-types')}: ${typesAsText}`

      return text
    },
  },
  methods: {
    ...mapActions(useImageStore, ['prepareNewImage']),
    triggerFileInput() {
      ;(this.$refs.fileInput as HTMLInputElement).click()
    },

    handleFileSelect(event: Event) {
      const input = event.target as HTMLInputElement
      const files = input.files

      if (!files || !files[0]) {
        return
      }

      this.setFile(files[0])

      // Reset so the same file can be selected again
      input.value = ''
    },

    handleDrop(event: DragEvent) {
      this.isDragging = false
      const files = event.dataTransfer?.files
      if (files && files[0]) {
        this.setFile(files[0])
      }
    },

    async setFile(file: File) {
      this.errorText = ''

      if (!file.type.startsWith('image/')) {
        this.errorText = this.$t('error.file.invalid-file-type') as string
        return
      }

      if (
        !this.allowedImageTypes.includes(file.type as TAllowedImageContentTypes)
      ) {
        this.errorText = this.$t('error.file.invalid-image-type') as string
        return
      }

      const imageObj = await this.prepareNewImage({
        image: file,
        maxDim: this.maxDim,
        uploadPath: this.uploadPath,
        fileNameFormat: this.fileNameFormat,
      })

      this.$emit('image-selected', imageObj as IImageBase)
    },

    onResetImageClick() {
      this.$emit('image-removed')
    },
  },
}
</script>

